import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { toast } from 'sonner';
import { Mail, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';

export default function ArenaxVerify() {
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const onVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const r = await api.post('/arenax/auth/verify-email', { email, code });
      const { access_token, user } = r.data;
      localStorage.setItem('outplay_token', access_token);
      localStorage.setItem('outplay_user', JSON.stringify(user));
      setToken(access_token);
      setUser(user);
      toast.success(`Welcome to ArenaX, ${user.name}!`);
      navigate('/arenax/player/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  return (
    <ArenaxLayout>
      <section className="min-h-[80vh] flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex h-12 w-12 rounded-2xl items-center justify-center mb-4"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              <ShieldCheck className="h-6 w-6 text-slate-950" />
            </div>
            <h1 className="text-3xl font-black text-slate-100 mb-2">Verify your email</h1>
            <p className="text-sm text-slate-500">We sent a 6-digit code to <span className="text-cyan-300">{email || 'your email'}</span></p>
          </div>

          <form onSubmit={onVerify} className="rounded-2xl p-7 border border-cyan-500/15 backdrop-blur-sm space-y-5 arenax-operational-form"
            style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
            <div>
              <label className="text-[11px] tracking-wider text-slate-400 uppercase font-bold">Verification Code</label>
              <input type="text" inputMode="numeric" maxLength={6}
                data-testid="arenax-verify-code"
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                className="arenax-input mt-2 w-full text-center text-3xl font-black tracking-[0.5em] py-4 rounded-xl"
                style={{ color: `hsl(var(--ax-accent-cyan))` }}
                placeholder="000000" />
            </div>
            <button type="submit" disabled={loading || code.length !== 6} data-testid="arenax-verify-submit"
              className="w-full px-4 py-3 rounded-xl font-bold text-slate-950 text-sm shadow-xl flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify & Enter ArenaX <ArrowRight className="h-4 w-4" /></>}
            </button>
            <div className="text-center text-xs text-slate-500">
              Didn't receive the code? <button type="button" onClick={() => navigate('/arenax/auth/register')} className="text-cyan-300 font-semibold">Resend or restart</button>
            </div>
          </form>
        </div>
      </section>
    </ArenaxLayout>
  );
}
